import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Heart, Globe, Leaf, ArrowRight, Sparkles } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const handleStartTest = () => {
    navigate('/test');
  };

  const features = [
    {
      icon: <Heart className="w-8 h-8 text-amber-500" />,
      title: "Comfort Connoisseur",
      description: "Discover if you're drawn to cozy, familiar flavors that warm the soul",
      image: "https://images.unsplash.com/photo-1617024805970-1b717c0b8b2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjb21mb3J0JTIwZm9vZHxlbnwwfHx8fDE3NTY3MTg2NjB8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Globe className="w-8 h-8 text-red-500" />,
      title: "Flavor Explorer", 
      description: "Find out if you crave bold, exotic cuisines from around the world",
      image: "https://images.unsplash.com/photo-1661939252817-ebb73304f4c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxleG90aWMlMjBjdWlzaW5lfGVufDB8fHx8MTc1NjcxODY2NHww&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Leaf className="w-8 h-8 text-green-500" />,
      title: "Wellness Warrior",
      description: "See if you prioritize fresh, wholesome ingredients and healthy choices",
      image: "https://images.unsplash.com/photo-1623452768745-de5f0d565c12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxjb2xvcmZ1bCUyMGZvb2QlMjB2YXJpZXR5fGVufDB8fHx8MTc1NjcxODY1NHww&ixlib=rb-4.1.0&q=85"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-yellow-400 via-orange-400 to-red-400 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-bounce">
                <Sparkles className="w-16 h-16 text-yellow-200" />
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
              Best Food Buddy
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
              Discover your unique food personality and get personalized recommendations 
              that match your taste preferences and dining style!
            </p>
            <Button 
              onClick={handleStartTest}
              className="bg-white text-orange-500 hover:bg-orange-50 text-lg px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Discover My Food Personality
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
        
        {/* Floating food elements */}
        <div className="absolute top-20 left-10 animate-float">
          <div className="w-12 h-12 bg-yellow-300 rounded-full opacity-60"></div>
        </div>
        <div className="absolute top-32 right-20 animate-float-delayed">
          <div className="w-8 h-8 bg-red-300 rounded-full opacity-60"></div>
        </div>
        <div className="absolute bottom-20 left-1/4 animate-float">
          <div className="w-10 h-10 bg-orange-300 rounded-full opacity-60"></div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              What's Your Food Personality?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Take our fun visual assessment and discover which of these three food personalities matches you best
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-0 bg-white/80 backdrop-blur-sm">
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    {feature.icon}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Meet Your Food Match?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            It only takes 2 minutes to discover your food personality and get personalized recommendations!
          </p>
          <Button 
            onClick={handleStartTest}
            className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Start My Food Journey
            <Sparkles className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;